import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
} from "react";
import { cn } from "../lib/cn";
import { Tooltip } from "./Tooltip";

/** Shared field chrome: a filled well with an inset hairline, accent ring on focus. */
export const fieldClass = cn(
  "w-full rounded-md bg-fill-1 text-body text-ink placeholder:text-ink-3",
  "shadow-[inset_0_0_0_1px_var(--vd-hairline)] outline-none",
  "transition-[box-shadow,background-color] duration-150 ease-out",
  "hover:shadow-[inset_0_0_0_1px_var(--vd-hairline-strong)]",
  "focus-visible:bg-transparent focus-visible:shadow-[inset_0_0_0_1px_var(--vd-focus),0_0_0_3px_var(--vd-accent-soft)] focus-visible:outline-none",
  "aria-invalid:shadow-[inset_0_0_0_1px_var(--vd-danger)]",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  leading?: ReactNode;
  trailing?: ReactNode;
  size?: "sm" | "md" | "lg";
  invalid?: boolean;
}

const heights = { sm: "h-7 text-ui", md: "h-8", lg: "h-10 text-title" } as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leading, trailing, size = "md", invalid, className, ...props },
  ref,
) {
  return (
    <div className={cn("relative flex items-center", className)}>
      {leading ? (
        <span className="pointer-events-none absolute left-2.5 flex text-ink-3 [&_svg]:size-4">
          {leading}
        </span>
      ) : null}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          fieldClass,
          heights[size],
          leading ? "pl-8" : "pl-3",
          trailing ? "pr-9" : "pr-3",
        )}
        {...props}
      />
      {trailing ? <span className="absolute right-1.5 flex items-center">{trailing}</span> : null}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /**
   * Grow with the text, from `rows` lines up to `maxRows`, then scroll. Follows typing, paste,
   * a controlled `value` and width changes. Code that sets `.value` on an uncontrolled box calls
   * `fitTextarea` afterwards.
   */
  autosize?: boolean;
  /** The most lines an autosized box shows before it scrolls. */
  maxRows?: number;
}

/**
 * Size an autosized textarea to its text: no taller than its `data-max-rows` lines, and
 * scrolling (with a scrollbar) only once it is at that height.
 */
export function fitTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  const maxRows = Number(el.dataset.maxRows) || 8;
  const cs = getComputedStyle(el);
  const px = (v: string) => Number.parseFloat(v) || 0;
  const line = px(cs.lineHeight) || 20;
  const padding = px(cs.paddingTop) + px(cs.paddingBottom);
  const border = px(cs.borderTopWidth) + px(cs.borderBottomWidth);
  // scrollHeight covers the text and padding; a border-box height also takes the border.
  const extra = cs.boxSizing === "border-box" ? border : -padding;
  const max = Math.ceil(maxRows * line + padding) + extra;
  // Measure from the smallest height so the box can shrink, then settle in one write.
  el.style.height = "auto";
  const want = el.scrollHeight + extra;
  el.style.height = `${Math.min(want, max)}px`;
  el.style.overflowY = want > max ? "auto" : "hidden";
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, rows = 3, autosize, maxRows = 8, onInput, ...props },
  ref,
) {
  const own = useRef<HTMLTextAreaElement | null>(null);
  const setRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      own.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  // A controlled value (or the first render) changes the text without an input event.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refit when the text or limit changes
  useLayoutEffect(() => {
    if (autosize) fitTextarea(own.current);
  }, [autosize, props.value, maxRows, rows]);

  // The window or a resized panel rewraps the text: refit on width changes only, so the
  // box's own height change does not loop back here.
  useEffect(() => {
    const el = own.current;
    if (!autosize || !el || typeof ResizeObserver === "undefined") return;
    let width = el.clientWidth;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => fitTextarea(el));
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [autosize]);

  return (
    <textarea
      ref={setRef}
      rows={rows}
      aria-invalid={invalid || undefined}
      data-max-rows={autosize ? maxRows : undefined}
      className={cn(fieldClass, "resize-none px-3 py-2 leading-5", className)}
      onInput={(e) => {
        if (autosize) fitTextarea(e.currentTarget);
        onInput?.(e);
      }}
      {...props}
    />
  );
});

export interface FieldProps {
  label: string;
  /** Short help shown on demand: rendered as the title of a small help mark next to the label. */
  help?: string;
  error?: string;
  /** Label sits beside the control instead of above it. */
  inline?: boolean;
  children: (id: string) => ReactNode;
  className?: string;
}

/** Label plus control. Errors replace nothing; they appear under the control in danger ink. */
export function Field({ label, help, error, inline, children, className }: FieldProps) {
  const id = useId();
  return (
    <div
      className={cn(
        inline
          ? "grid grid-cols-[minmax(96px,auto)_1fr] items-center gap-3"
          : "flex flex-col gap-1.5",
        className,
      )}
    >
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="font-medium text-caption text-ink-2">
          {label}
        </label>
        {help ? (
          <Tooltip content={help}>
            <button
              type="button"
              aria-label={`About ${label}`}
              className="inline-flex size-3.5 cursor-help items-center justify-center rounded-pill bg-fill-2 text-[9px] text-ink-3 hover:text-ink"
            >
              ?
            </button>
          </Tooltip>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        {children(id)}
        {error ? <p className="text-caption text-danger-ink">{error}</p> : null}
      </div>
    </div>
  );
}
